import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';

const articleDetailImages: Record<string, string> = {
  '2025-global-market-trends': 'https://readdy.ai/api/search-image?query=Futuristic%20world%20map%20with%20glowing%20trade%20routes%20and%20rising%20graphs%20modern%20business%20visualization%20warm%20golden%20and%20teal%20tones%20professional&width=1200&height=600&seq=art-detail-1&orientation=landscape',
  'cross-border-cooperation-success-case': 'https://readdy.ai/api/search-image?query=Successful%20business%20meeting%20between%20Asian%20and%20Southeast%20Asian%20executives%20in%20modern%20office%20handshake%20moment%20warm%20professional%20setting&width=1200&height=600&seq=art-detail-2&orientation=landscape',
  'international-brand-building-guide': 'https://readdy.ai/api/search-image?query=Elegant%20brand%20identity%20presentation%20with%20Chinese%20elements%20merged%20with%20international%20design%20modern%20creative%20studio%20warm%20sophisticated&width=1200&height=600&seq=art-detail-3&orientation=landscape',
  'digital-globalization-ecommerce': 'https://readdy.ai/api/search-image?query=Modern%20ecommerce%20fulfillment%20center%20with%20automated%20sorting%20systems%20glowing%20screens%20showing%20global%20orders%20warm%20professional%20lighting&width=1200&height=600&seq=art-detail-4&orientation=landscape',
  'overseas-talent-strategy': 'https://readdy.ai/api/search-image?query=Diverse%20international%20team%20collaborating%20in%20modern%20open%20office%20with%20world%20map%20on%20wall%20warm%20natural%20lighting%20professional%20atmosphere&width=1200&height=600&seq=art-detail-5&orientation=landscape',
  'global-compliance-risk-management': 'https://readdy.ai/api/search-image?query=Professional%20legal%20documents%20and%20compliance%20checklist%20on%20modern%20desk%20with%20globe%20in%20background%20warm%20sophisticated%20office%20setting&width=1200&height=600&seq=art-detail-6&orientation=landscape',
};

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/articles" replace />;
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 2);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://exportr.github.io/article/${article.slug}`}
        ogType="article"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            'headline': article.title,
            'description': article.excerpt,
            'author': { '@type': 'Person', 'name': article.author },
            'datePublished': article.date,
            'image': articleDetailImages[article.slug],
            'publisher': {
              '@type': 'Organization',
              'name': '征途国际',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            'itemListElement': [
              { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://exportr.github.io' },
              { '@type': 'ListItem', 'position': 2, 'name': '新闻洞察', 'item': 'https://exportr.github.io/articles' },
              { '@type': 'ListItem', 'position': 3, 'name': article.title, 'item': `https://exportr.github.io/article/${article.slug}` },
            ],
          },
        ]}
      />

      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        <article className="bg-background-100/70">
          <div className="w-full px-6 md:px-10 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '新闻洞察', path: '/articles' },
                  { label: article.title },
                ]}
              />

              <div className="bg-background-50 border border-background-200/60 rounded-lg overflow-hidden">
                <div className="h-64 md:h-80 overflow-hidden">
                  <img
                    src={articleDetailImages[article.slug]}
                    alt={article.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="p-6 md:p-10">
                  <div className="flex items-center flex-wrap gap-3 mb-4">
                    <span className="text-xs px-2.5 py-1 bg-accent-100/80 text-accent-700 rounded-full font-medium">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-400">{article.date}</span>
                    <span className="text-xs text-foreground-400">阅读 {article.readTime}</span>
                    <span className="text-xs text-foreground-400">作者：{article.author}</span>
                  </div>

                  <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 font-heading mb-6 leading-tight">
                    {article.title}
                  </h1>

                  <div
                    className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:text-foreground-950 [&>h3]:mt-8 [&>h3]:mb-3 [&>h3]:font-heading [&>p]:mb-4 [&>p]:leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  <div className="mt-8 pt-6 border-t border-background-200/60">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="text-xs text-foreground-400 mr-2">标签：</span>
                      {article.tags.map((tag) => (
                        <span key={tag} className="text-xs px-2.5 py-1 bg-secondary-100/80 text-secondary-700 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {relatedArticles.length > 0 && (
                <div className="mt-10">
                  <h4 className="text-base font-semibold text-foreground-950 mb-4 font-heading">
                    相关文章
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.slug}
                        to={`/article/${ra.slug}`}
                        className="group bg-background-50 border border-background-200/60 rounded-lg p-5 hover:border-primary-300/40 transition-all cursor-pointer"
                      >
                        <h4 className="text-sm font-semibold text-foreground-950 mb-2 font-heading group-hover:text-primary-600 transition-colors line-clamp-2">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{ra.excerpt}</p>
                        <span className="text-xs text-foreground-400">{ra.date} · {ra.readTime}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}